import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { useApp, RecentScan } from '@/context/AppContext';
import { resolveAssetByBarcode } from '@/data/mockAssets';
import { Asset } from '@/types/models';
import { AssetQuickViewDrawer } from '@/components/assets/AssetQuickViewDrawer';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Camera,
  Keyboard,
  PenLine,
  Flashlight,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  ScanBarcode,
} from 'lucide-react';
import { cn } from '@/lib/utils';

type ScanResultState = 'idle' | 'resolving' | 'found' | 'not_found' | 'invalid' | 'error';

export function ScanModal() {
  const { scanModalOpen, setScanModalOpen, recentScans, addRecentScan } = useApp();
  const navigate = useNavigate();

  const [tab, setTab] = useState<string>('camera');
  const [resultState, setResultState] = useState<ScanResultState>('idle');
  const [resolvedAsset, setResolvedAsset] = useState<Asset | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const [usbInput, setUsbInput] = useState('');
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const scannerRef = useRef<Html5Qrcode | null>(null);
  const usbInputRef = useRef<HTMLInputElement>(null);
  const scannerContainerId = 'qr-scanner-viewport';

  // Reset state when modal opens
  useEffect(() => {
    if (scanModalOpen) {
      setResultState('idle');
      setResolvedAsset(null);
      setManualInput('');
      setUsbInput('');
      setCameraError(null);
    } else {
      stopCamera();
    }
  }, [scanModalOpen]);

  // Auto-focus USB input when tab changes
  useEffect(() => {
    if (tab === 'usb' && usbInputRef.current) {
      usbInputRef.current.focus();
    }
  }, [tab]);

  // Start camera when tab = camera
  useEffect(() => {
    if (tab === 'camera' && scanModalOpen) {
      startCamera();
    } else {
      stopCamera();
    }
    return () => { stopCamera(); };
  }, [tab, scanModalOpen]);

  const startCamera = async () => {
    setCameraError(null);
    // Small delay to ensure DOM element exists
    await new Promise((r) => setTimeout(r, 200));

    const el = document.getElementById(scannerContainerId);
    if (!el) return;

    try {
      const scanner = new Html5Qrcode(scannerContainerId);
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          handleScanResult(decodedText);
          stopCamera();
        },
        () => {} // ignore scan failures
      );
      setCameraActive(true);
    } catch (err: any) {
      setCameraError(
        err?.message?.includes('Permission')
          ? 'Camera permission denied. Please allow camera access in your browser settings.'
          : 'Could not access camera. Make sure no other app is using it.'
      );
      setCameraActive(false);
    }
  };

  const stopCamera = useCallback(async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      } catch {}
      scannerRef.current = null;
    }
    setCameraActive(false);
  }, []);

  const handleScanResult = async (barcode: string) => {
    const trimmed = barcode.trim();
    if (!trimmed) return;

    // Extract asset UID from resolver URL if present
    const urlMatch = trimmed.match(/\/i\/(.+)$/);
    const assetUid = urlMatch ? urlMatch[1] : trimmed;

    setResultState('resolving');

    try {
      const asset = await resolveAssetByBarcode(assetUid);
      const scan: RecentScan = {
        id: crypto.randomUUID(),
        barcode: assetUid,
        entityType: asset ? 'asset' : 'unknown',
        label: asset ? `${asset.category_name} — ${asset.manufacturer_name} ${asset.model}` : undefined,
        timestamp: new Date().toISOString(),
        success: !!asset,
      };
      addRecentScan(scan);

      if (asset) {
        setResolvedAsset(asset);
        setResultState('found');
      } else {
        setResultState('not_found');
      }
    } catch {
      setResultState('error');
    }
  };

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualInput.trim()) handleScanResult(manualInput.trim());
  };

  const handleUsbKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && usbInput.trim()) {
      handleScanResult(usbInput.trim());
      setUsbInput('');
    }
  };

  const openAssetDrawer = () => {
    setDrawerOpen(true);
  };

  const navigateToAsset = () => {
    if (resolvedAsset) {
      setScanModalOpen(false);
      navigate(`/assets/${resolvedAsset.id}`);
    }
  };

  return (
    <>
      <Dialog open={scanModalOpen} onOpenChange={setScanModalOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScanBarcode className="h-5 w-5 text-primary" />
              Scan Asset
            </DialogTitle>
          </DialogHeader>

          {/* Scan Method Tabs */}
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="camera" className="gap-2 text-xs sm:text-sm">
                <Camera className="h-4 w-4" /> Camera
              </TabsTrigger>
              <TabsTrigger value="usb" className="gap-2 text-xs sm:text-sm">
                <Keyboard className="h-4 w-4" /> USB Scanner
              </TabsTrigger>
              <TabsTrigger value="manual" className="gap-2 text-xs sm:text-sm">
                <PenLine className="h-4 w-4" /> Manual
              </TabsTrigger>
            </TabsList>

            {/* Camera Tab */}
            <TabsContent value="camera" className="mt-4">
              {cameraError ? (
                <div className="flex flex-col items-center gap-3 py-8 text-center">
                  <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
                    <AlertTriangle className="h-6 w-6 text-destructive" />
                  </div>
                  <p className="text-sm text-muted-foreground max-w-xs">{cameraError}</p>
                  <Button variant="outline" size="sm" onClick={startCamera}>
                    Retry
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div
                    id={scannerContainerId}
                    className="w-full aspect-square max-h-[300px] rounded-lg overflow-hidden bg-muted border border-border"
                  />
                  {cameraActive && (
                    <p className="text-xs text-muted-foreground text-center">
                      Point camera at a QR code or barcode
                    </p>
                  )}
                </div>
              )}
            </TabsContent>

            {/* USB Scanner Tab */}
            <TabsContent value="usb" className="mt-4">
              <div className="flex flex-col items-center gap-4 py-6">
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Keyboard className="h-8 w-8 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground text-center max-w-xs">
                  Focus this field and scan with your USB barcode scanner. The code will be captured automatically on Enter.
                </p>
                <Input
                  ref={usbInputRef}
                  value={usbInput}
                  onChange={(e) => setUsbInput(e.target.value)}
                  onKeyDown={handleUsbKeyDown}
                  placeholder="Waiting for scanner input…"
                  className="text-center font-mono text-lg max-w-xs"
                  autoFocus
                />
              </div>
            </TabsContent>

            {/* Manual Entry Tab */}
            <TabsContent value="manual" className="mt-4">
              <form onSubmit={handleManualSubmit} className="space-y-4">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Enter an asset ID, barcode, serial number, or resolver URL.
                  </p>
                  <Input
                    value={manualInput}
                    onChange={(e) => setManualInput(e.target.value)}
                    placeholder="e.g. ET-PHX-000000001"
                    className="font-mono"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={!manualInput.trim()}>
                  Look Up
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <Separator className="my-2" />

          {/* Scan Result State */}
          <ScanResultBanner
            state={resultState}
            asset={resolvedAsset}
            onViewQuick={openAssetDrawer}
            onViewFull={navigateToAsset}
            onReset={() => setResultState('idle')}
          />

          {/* Recent Scans */}
          {recentScans.length > 0 && (
            <div className="mt-2">
              <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                Recent Scans
              </h4>
              <div className="space-y-1.5 max-h-[160px] overflow-y-auto">
                {recentScans.slice(0, 8).map((scan) => (
                  <button
                    key={scan.id}
                    onClick={() => handleScanResult(scan.barcode)}
                    className="flex items-center gap-2 w-full rounded-md px-3 py-2 text-left text-sm hover:bg-accent transition-colors"
                  >
                    {scan.success ? (
                      <CheckCircle2 className="h-4 w-4 text-status-complete shrink-0" />
                    ) : (
                      <XCircle className="h-4 w-4 text-destructive shrink-0" />
                    )}
                    <span className="font-mono text-foreground">{scan.barcode}</span>
                    {scan.label && (
                      <span className="text-muted-foreground truncate ml-auto text-xs">{scan.label}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AssetQuickViewDrawer
        asset={resolvedAsset}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </>
  );
}

function ScanResultBanner({
  state,
  asset,
  onViewQuick,
  onViewFull,
  onReset,
}: {
  state: ScanResultState;
  asset: Asset | null;
  onViewQuick: () => void;
  onViewFull: () => void;
  onReset: () => void;
}) {
  if (state === 'idle') return null;

  return (
    <div
      className={cn(
        'rounded-lg border p-3',
        state === 'resolving' && 'border-primary/30 bg-primary/5',
        state === 'found' && 'border-status-complete/30 bg-status-complete/5',
        state === 'not_found' && 'border-destructive/30 bg-destructive/5',
        state === 'invalid' && 'border-destructive/30 bg-destructive/5',
        state === 'error' && 'border-destructive/30 bg-destructive/5',
      )}
    >
      {state === 'resolving' && (
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <span className="text-sm text-foreground">Resolving asset…</span>
        </div>
      )}

      {state === 'found' && asset && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-status-complete" />
            <span className="text-sm font-semibold text-foreground">Asset Found</span>
            <Badge variant="secondary" className="ml-auto font-mono text-xs">
              {asset.barcode || asset.internal_barcode}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            {asset.category_name} — {asset.manufacturer_name} {asset.model}
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={onViewQuick}>
              Quick View
            </Button>
            <Button size="sm" onClick={onViewFull}>
              Full Detail
            </Button>
          </div>
        </div>
      )}

      {state === 'not_found' && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-destructive" />
            <span className="text-sm text-foreground">Asset not found</span>
          </div>
          <Button size="sm" variant="ghost" onClick={onReset}>
            Dismiss
          </Button>
        </div>
      )}

      {state === 'error' && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <span className="text-sm text-foreground">Network error — try again</span>
          </div>
          <Button size="sm" variant="ghost" onClick={onReset}>
            Dismiss
          </Button>
        </div>
      )}
    </div>
  );
}
